<template>
    <div id="signature" class="signature-container">
        <a-spin :spinning="loading">
            <div class="signature-header">
                <h2>Mail İmzası</h2>
                <p class="signature-description">E-postalarınıza otomatik olarak eklenecek imzanızı burada düzenleyebilirsiniz.</p>
            </div>

            <div class="mail-selection-container">
                <div class="mail-selection">
                    <label>E-posta Adresi Seçimi</label>
                    <a-select
                        v-model="selectedMail"
                        placeholder="E-posta adresinizi seçiniz"
                        class="mail-select"
                        @change="onMailSelect"
                    >
                        <a-select-option :value="mail.mail" v-for="mail in mailOptions" :key="mail._id">
                            {{ mail.mail }}
                        </a-select-option>
                    </a-select>
                </div>
            </div>

            <div class="auto-add-container" v-if="selectedMail">
                <a-checkbox
                    v-model="isAutoAdd"
                    :disabled="editorContent === ''"
                    @change="changeSignatureAuto"
                >
                    Otomatik Ekle
                </a-checkbox>
                <a-tooltip placement="top">
                    <template #title>Bu seçenek işaretlendiğinde, bu imza yeni e-postalarınıza otomatik olarak eklenecektir.</template>
                    <a-icon type="info-circle" class="info-icon" />
                </a-tooltip>
            </div>

            <div class="signature-editor-container" v-if="selectedMail">
                <h3>İmza Düzenleyici</h3>
                <MailEditor 
                    v-model="editorContent"
                    ref="mailEditor"
                    @input="onEditorChange"
                    :inlineTextOnly="true"
                    :hideAIAssistant="true"
                />
            </div>

            <div class="action-buttons" v-if="selectedMail">
                <SupsisButton
                    primary
                    @click="saveSignature"
                >
                    Kaydet
                </SupsisButton>
            </div>
        </a-spin>
    </div>
</template>

<script>
import Vue from "vue";
import { mapState, mapGetters, mapActions } from "vuex";
import { log2 } from "../../../../utils/log2";
import MailEditor from "../../Mail/new/components/MailEditor.vue";
import SupsisButton from "../../Settings/components/SupsisButton.vue";

const log = log2("Signature.vue");
export default Vue.extend({
    name: "Signature",
    components: { MailEditor, SupsisButton },
    data() {
        return {
            loading: false,
            editorContent: "",
            contentHtml: "",
            mailAddress: "",
            selectedId: this.$route.params.id,
            isAutoAdd: false,
            mailOptions: [],
            selectedMail: "",
            signatureLoaded: false,
        };
    },
    computed: {
        ...mapState({
            allMailAddresses: (state) => state.mail.allMailAddresses,
            user: (state) => state.auth.user,
        }),
        ...mapGetters({}),
    },
    methods: {
        ...mapActions({
            getSenderMailAddress: "mail/getSenderMailAddress",
            saveSignatureAction: "mail/saveSignature",
            getMailSignature: "mail/getMailSignature",
            changeSignatureAutoUse: "mail/changeSignatureAutoUse",
        }),
        onMailSelect() {
            this.editorContent = "";
            this.contentHtml = "";
            this.signatureLoaded = false;
            this.isAutoAdd = false;
            
            if (this.$refs.mailEditor && this.$refs.mailEditor.editor) {
                this.$refs.mailEditor.editor.commands.setContent("");
            }
            
            this.fetchSignatureForMail();
        },
        async fetchSignatureForMail() {
            if (!this.selectedMail) return;
            
            this.loading = true;
            try {
                this.editorContent = "";
                this.contentHtml = "";
                
                if (this.$refs.mailEditor && this.$refs.mailEditor.editor) {
                    this.$refs.mailEditor.editor.commands.setContent("");
                }
                
                const signature = await this.getMailSignature({ mailAddress: this.selectedMail });
                
                if (signature && signature.html) {
                    this.editorContent = signature.html;
                    this.contentHtml = signature.html;
                    this.isAutoAdd = signature.isAutoAdd || false;
                    this.signatureLoaded = true;
                    
                    this.$nextTick(() => {
                        if (this.$refs.mailEditor && this.$refs.mailEditor.editor) {
                            this.$refs.mailEditor.editor.commands.setContent(this.editorContent);
                        }
                    });
                } else {
                    this.editorContent = "";
                    this.contentHtml = "";
                    this.isAutoAdd = false;
                    this.signatureLoaded = true;
                    
                    this.$nextTick(() => {
                        if (this.$refs.mailEditor && this.$refs.mailEditor.editor) {
                            this.$refs.mailEditor.editor.commands.setContent("");
                        }
                    });
                }
            } catch (error) {
                console.error("İmza yüklenirken hata oluştu:", error);
                this.$message.error("İmza yüklenirken hata oluştu");
                this.signatureLoaded = false;
                
                this.editorContent = "";
                this.contentHtml = "";
                
                this.$nextTick(() => {
                    if (this.$refs.mailEditor && this.$refs.mailEditor.editor) {
                        this.$refs.mailEditor.editor.commands.setContent("");
                    }
                });
            } finally {
                this.loading = false;
            }
        },
        changeSignatureAuto() {
            if (!this.selectedMail) {
                this.$message.error("Lütfen bir e-posta adresi seçin");
                return;
            }
            
            this.loading = true;
            this.changeSignatureAutoUse({ mailAddress: this.selectedMail, isAutoAdd: this.isAutoAdd })
                .then(() => {
                    this.$message.success("Otomatik imza ayarı değiştirildi");
                })
                .catch(error => {
                    console.error("Otomatik imza ayarı değiştirilirken hata:", error);
                    this.$message.error("Otomatik imza ayarı değiştirilirken hata oluştu");
                    this.isAutoAdd = !this.isAutoAdd;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        onEditorChange(content) {
            this.contentHtml = content;
            this.editorContent = content;
        },
        async saveSignature() {
            if (!this.selectedMail) {
                this.$message.error("Lütfen bir e-posta adresi seçin");
                return;
            }

            this.loading = true;
            try {
                const htmlContent = this.contentHtml || this.editorContent || "";
                
                const result = await this.saveSignatureAction({
                    mailAddress: this.selectedMail,
                    html: htmlContent,
                });

                if (result === true) {
                    this.$message.success("İmza başarıyla kaydedildi");
                    await this.fetchSignatureForMail();
                } else {
                    throw new Error("İmza kaydedilemedi");
                }
            } catch (error) {
                console.error("İmza kaydedilirken hata:", error);
                this.$message.error("İmza kaydedilirken hata oluştu");
            } finally {
                this.loading = false;
            }
        },
        getAndSetSelectedMailAddresses() {
            this.loading = true;
            this.getSenderMailAddress({
                userId: this.user._id,
                selectedMailAddressId: this.mailAddress._id,
                forwardTo: this.mailAddress.mail,
            })
                .then((res) => {
                    this.mailOptions = res.mailAddresses;
                    if (this.mailOptions.map((mail) => mail.mail).includes(this.mailAddress.mail) === false) {
                        this.mailOptions = [
                            { _id: this.mailAddress._id, mail: this.mailAddress.mail },
                            ...this.mailOptions,
                        ];
                    }
                })
                .catch(error => {
                    console.error("Mail adresleri alınırken hata:", error);
                    this.$message.error("Mail adresleri alınırken hata oluştu");
                })
                .finally(() => {
                    this.loading = false;
                });
        },
    },
    async mounted() {
        this.loading = true;
        try {
            this.mailAddress = this.allMailAddresses.find(
                (email) => email._id === this.selectedId || (email.department && email.department._id === this.selectedId)
            );
            
            if (this.mailAddress) {
                this.selectedMail = this.mailAddress.mail;
                this.getAndSetSelectedMailAddresses();
                
                setTimeout(() => {
                    this.fetchSignatureForMail();
                }, 300);
            } else {
                this.editorContent = "";
                this.contentHtml = "";
                this.isAutoAdd = false;
            }
        } catch (error) {
            console.error("Sayfa yüklenirken hata:", error);
            this.$message.error("Sayfa yüklenirken hata oluştu");
            this.editorContent = "";
            this.contentHtml = "";
            this.isAutoAdd = false;
        } finally {
            this.loading = false;
        }
    },
    watch: {
        async allMailAddresses(value) {
            if (value && value.length > 0) {
                this.mailAddress = value.find(
                    (email) => email._id === this.selectedId || (email.department && email.department._id === this.selectedId)
                );
                if (this.mailAddress) {
                    this.selectedMail = this.mailAddress.mail;
                    
                    this.editorContent = "";
                    this.contentHtml = "";
                    this.isAutoAdd = false;
                    
                    await this.fetchSignatureForMail();
                } else {
                    this.editorContent = "";
                    this.contentHtml = "";
                    this.isAutoAdd = false;
                }
            }
        },
    },
});
</script>

<style scoped lang="scss">
.signature-container {
    padding: 30px;
    background-color: #ffffff;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    width: 80%;
    max-width: 1000px;
    margin: 0 auto;

    .signature-header {
        margin-bottom: 24px;
        
        h2 {
            font-size: 24px;
            font-weight: 600;
            color: #22272d;
            margin-bottom: 8px;
        }
        
        .signature-description {
            color: #666;
            font-size: 14px;
        }
    }

    .mail-selection-container {
        margin-bottom: 24px;
        
        .mail-selection {
            display: flex;
            flex-direction: column;
            gap: 8px;
            
            label {
                font-weight: 500;
                color: #333;
                font-size: 14px;
            }
            
            .mail-select {
                width: 100%;
                max-width: 400px;
            }
        }
    }

    .auto-add-container {
        display: flex;
        align-items: center;
        margin-bottom: 20px;
        
        .info-icon {
            margin-left: 8px;
            color: #999;
        }
    }

    .signature-editor-container {
        margin-bottom: 30px;
        
        h3 {
            font-size: 16px;
            font-weight: 500;
            margin-bottom: 12px;
            color: #333;
        }
    }

    .action-buttons {
        display: flex;
        justify-content: flex-end;
        margin-top: 20px;
    }
}
</style>
